import React, { useEffect, useState, useRef } from 'react';
import { MeetingSMLRer } from '../src/components/MeetingSMLRer/MeetingSMLRer';
import { BrowserRouter as Router, Redirect, Route, Switch, } from "react-router-dom";
import { adjustPresentersTime } from './TimeHelper';
import { useCookies } from "react-cookie";
import { v4 as uuidv4 } from 'uuid';
import { LandingPage } from "../src/components/LandingPage/LandingPage"
import { Backdrop, CircularProgress } from '@mui/material';


import { getTimeBuffer } from './API';
import {
  ensureSeriesSession,
  getLiveSessionState,
  getSessionStateById,
  postSessionCommands,
  endSessionAndSave,
} from './meeting/v2Api';
import { applySessionToAppState, slotToPresenter } from './meeting/stateAdapter';
import { Command } from './meeting/types';
import { stateUpdateToCommands } from './meeting/commandMapper';
import { parseWsMessage, sendWsJoin, sendWsPing } from './meeting/wsSync';
import { SessionState } from './meeting/types';
import axios from 'axios';


import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';


const theme = createTheme();

const DEFAULT_BONUS_TIME: Presenter = {
  name: 'Bonus Time',
  id: '-',
  description: '',
  flight: 0,
  priority: -1,
  slotType: "Break",
  initialDuration: 0,
  currentDuration: 0,
  elapsedTime: 0,
  startTime: "",
  endTime: "",
  dbElapsedTime: 0
}

let ws = new WebSocket(import.meta.env.VITE_WEBSOCKET_ENDPOINT || "wss://smlr.reshci-c-uw2.cloudos.autodesk.com/api/");

const wsReconnect = () => {
  ws = new WebSocket(import.meta.env.VITE_WEBSOCKET_ENDPOINT || "wss://smlr.reshci-c-uw2.cloudos.autodesk.com/api/")
  console.log("ws state: ", ws.readyState)
}

const App: React.FC = () => {
  const [meetingName, setMeetingName] = useState<string>("");
  const [meetingLength, setMeetingLength] = useState<number>(0);
  const [presenters, setPresenters] = useState<Presenter[]>([]);
  const [bonusTime, setBonusTime] = useState<Presenter>({ ...DEFAULT_BONUS_TIME });
  const [cursor, setCursor] = useState<number>(-1);
  const [prevCursor, setPrevCursor] = useState<number>(-1);
  const [isDebugModeEnabled, setIsDebugModeEnabled] = useState<boolean>(false);
  const [presenterOrder, setPresenterOrder] = useState<string[]>([]);
  const [cookies, setCookie] = useCookies(["user"]);

  const [elapsedTimeTrigger, setElapsedTimeTrigger] = useState(false)
  const [viewingSessionId, setViewingSessionId] = useState<string | null>(null);
  const [meetingStartTime, setMeetingStartTime] = useState<any>("")
  const [meetingEndTime, setMeetingEndTime] = useState<any>("")
  const [isDisplayTimeUnitSeconds, setIsDisplayTimeUnitSeconds] = useState<boolean>(true);
  const [homepageMeetingID, setHomepageMeetingID] = useState<string>("")
  const [meetingID, setMeetingID] = useState<string>("")
  const [loading, setLoading] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false);

  const sessionId = useRef<string>("");
  const [liveSessionId, setLiveSessionId] = useState("");
  const [sessionPersistReady, setSessionPersistReady] = useState(false);
  const sessionRevision = useRef<number>(0);
  const persistChain = useRef<Promise<void>>(Promise.resolve());
  const timeBuffer = useRef<number>(0);
  const syncGeneration = useRef(0);
  // MEETING FUNCTIONS ============================================================================

  const getMeetingIDFromURL = (): string => {
    let urlString = window.location.pathname;
    return (urlString.split("/").length >= 2)? urlString.split("/")[1] : "";
  }

  const setSessionId = ():void => {
    if (sessionId.current === "") {
      sessionId.current = uuidv4();
    }
  }

  const setUserIdCookie = ():void => {
    if (!cookies.user) {
      setCookie("user", uuidv4(), { path: "/" });
    }
  }

  const refreshApp = (): void => {

    let meetingID = getMeetingIDFromURL();
    if (meetingID === "" || meetingID === "home") {
      meetingID = homepageMeetingID;
    }
    setMeetingID(decodeURI(meetingID));
    updateStateFromDatabase(meetingID);
  }

  const websocketPrinter = ():void => {
    console.log("Connected to ws server");
  }

  useEffect(() => {
    if (ws.readyState === 3) {
      wsReconnect()
    }
  }, [])

  useEffect(() => {
    refreshApp();
  }, [])

    /**
   * function we want to trigger on start
   *And tied it to the state which updates when a meeting is created in the homepage. So it will fire -
   *on initial render of the app, and when a new meeting is created. Manipulating the MEETING_ID instead -
   *of the current url. This is updated through the state that we passed to it (homepageMeetingId).
   */

  useEffect(() => {
    syncGeneration.current += 1;
    const generation = syncGeneration.current;
    setLiveSessionId("");
    setSessionPersistReady(false);
    sessionRevision.current = 0;
    setLoading(true);
    syncSessionFromServer(undefined, undefined, generation);
  }, [meetingID, viewingSessionId])

  //HeartBeat
  useEffect(() => {
    if (meetingID === "" || meetingID === "home") return;
    setUserIdCookie()
    setSessionId();
    setUserTimeBuffer();


    const onOpen = () => {
      websocketPrinter();
      sendWsJoin(ws, meetingID, sessionId.current);
    };
    ws.addEventListener('open', onOpen);
    sendWsJoin(ws, meetingID, sessionId.current);

    let newElapsedTimeTrigger = elapsedTimeTrigger;
    let interval = setInterval(() => {
      newElapsedTimeTrigger = !newElapsedTimeTrigger;
      setElapsedTimeTrigger(newElapsedTimeTrigger);
    }, 1000)

    let websocketInterval = setInterval(() => {
      sendWsPing(ws);
    }, 30000)

    return () => {
      clearInterval(interval)
      clearInterval(websocketInterval)
      ws.removeEventListener('open', onOpen);
    }
    
  }, [homepageMeetingID, meetingID])
  // ==============================================================================================

  // PRESENTERS FUNCTIONS =========================================================================


  const getPresenterById = (presentersList: Presenter[], id: string): Presenter | undefined => {
    const presenter: Presenter | undefined = presentersList.find(p => {
      return p.id === id;
    });
    return presenter;
  }

    /**
   * 
   * @param unorderedPresenters List of presenters
   * @returns Ordered list of presenters. 
   * 
   */
     const orderPresenters = (unorderedPresenters: Presenter[], meetingOrder: string[]): Presenter[] => {
      const seen = new Set();
      const newPresenters: Presenter[] = [];
      for (let i = 0; i < meetingOrder.length; i++) {
        const presenter: Presenter | undefined = getPresenterById(unorderedPresenters, meetingOrder[i]);
        if (presenter && !seen.has(presenter.id)) newPresenters.push(presenter);
        if (presenter) seen.add(presenter.id)
      }
  
      const missedPresneters = unorderedPresenters.filter(p => !seen.has(p.id));
      return newPresenters.concat(missedPresneters);
    }

    const deleteAllPresenters = (): void => {
      writeMeetingChange({
        presenterDelete: presenters.map((p) => ({ ...p })),
        presentersUpdateAll: [],
        presenterOrder: [],
      });
    }
  // ==============================================================================================

  // TIME FUNCTIONS ===============================================================================

  const calculateBonusTime = (newPresenters: Presenter[], meetingDuration: number, cursor: number, oldBonusTime?: Presenter) => {

    // Calculate how long the the meeting will go given everyone's current time
    let cumulativeTime = 0;
    for (let i = 0; i < newPresenters.length; i++) {
      if (i < cursor) {
        cumulativeTime += newPresenters[i].elapsedTime;
      } else if (i === cursor) {
        cumulativeTime += Math.max(newPresenters[i].elapsedTime, newPresenters[i].currentDuration);
      } else {
        cumulativeTime += newPresenters[i].currentDuration;
      }
    }

    const newBonusTime = (oldBonusTime !== undefined) ? oldBonusTime : { ...bonusTime };
    newBonusTime.currentDuration = Math.max(meetingDuration - cumulativeTime, 0);
    return newBonusTime;
  }

  async function setUserTimeBuffer() {

    //might become inaccurate as we get more clients + server (its a problem is some clients experience more delay than others, and thus a bigger buffer)
    await getTimeBuffer(meetingID).then(res => {
      let current: any = new Date();
      let serverTime: any = new Date(res.data.serverTime);
      timeBuffer.current = serverTime.getTime() - current.getTime();    //we add the buffer to the local current time, to get a universal server current time
    }).catch((err) => { console.warn("FAILED TO GET TIME BUFFER", err) });
  }
  // ==============================================================================================


  // UPDATING STATE FUNCTIONS =====================================================================
  const updateCursor = (newValue: number): void => {
    setPrevCursor(cursor);
    setCursor(newValue);
  }

  // here we read in WS messages, 
  // the the only messages we should be getting are those telling us to check database and update states
  // TODO: KENDRA consider updating locally before reading from the database
  ws.onmessage = (event) => {
    const message = parseWsMessage(event.data);
    if (!message) return;

    if ('type' in message && message.type === 'state' && message.state) {
      const seriesId = message.seriesId ?? message.meetingId;
      if (seriesId === meetingID && message.senderId !== sessionId.current) {
        applyServerSession(message.state, cursor);
      }
      return;
    }

    if ('meetingId' in message && message.meetingId === meetingID && message.senderId !== sessionId.current) {
      syncSessionFromServer();
    }
  }

  const resolveUpdateImplications = (valuesToUpdate: StateUpdateObject): StateUpdateObject => {

    // PRESENTER UPDATE
    if (valuesToUpdate.presenterUpdate !== undefined) {
      let updatedPresenters: Presenter[] = valuesToUpdate.presentersUpdateAll || [...presenters];
      for (let i = 0; i < updatedPresenters.length; i++) {
        for (let j = 0; j < valuesToUpdate.presenterUpdate.length; j++) {
          if ( updatedPresenters[i].id === valuesToUpdate.presenterUpdate[j].id ) {
            updatedPresenters[i] = {...valuesToUpdate.presenterUpdate[j]}
          }
        }
      }
      valuesToUpdate.presentersUpdateAll = updatedPresenters;
    }

    //PRESENTER ADD
    if (valuesToUpdate.presenterAdd !== undefined) {
      let updatedPresenters: Presenter[] = valuesToUpdate.presentersUpdateAll || [...presenters];
      let updatedOrder: string[] = valuesToUpdate.presenterOrder || [...presenterOrder];

      // Add the presenters, ensuring if duplicated the new value is used
      for (let i = 0; i < valuesToUpdate.presenterAdd.length; i++) {
        let index = updatedPresenters.map( p => p.id).indexOf(valuesToUpdate.presenterAdd[i].id)
        if ( index === -1 ) {
          updatedPresenters.push(valuesToUpdate.presenterAdd[i])
          updatedOrder.push(valuesToUpdate.presenterAdd[i].id)
        } else {
          updatedPresenters[index] = {...valuesToUpdate.presenterAdd[i]}
          //KENDRA TODO: this shouldn't really happen, if it does should the user know? the logs?
        }
      }

      valuesToUpdate = { ...valuesToUpdate, presentersUpdateAll: updatedPresenters, presenterOrder: updatedOrder };
    }

    //PRESENTER DELETE
    if (valuesToUpdate.presenterDelete !== undefined) {
      let newPresenters = valuesToUpdate.presentersUpdateAll || [...presenters]
      let updatedOrder: string[] = valuesToUpdate.presenterOrder || [...presenterOrder];

      for (let j = newPresenters.length - 1; j >= 0; j--){
        for (let i = 0; i < newPresenters.length; i++) {
          if (valuesToUpdate.presenterDelete && valuesToUpdate.presenterDelete[j] && newPresenters[i].id === valuesToUpdate.presenterDelete[j].id) {
            newPresenters.splice(i, 1);
            valuesToUpdate = {...valuesToUpdate, presentersUpdateAll: [...newPresenters]};
            break;
          }
        }
        for (let k = updatedOrder.length - 1; k >= 0; k--) {
          if (valuesToUpdate.presenterDelete && valuesToUpdate.presenterDelete[j] && updatedOrder[k] === valuesToUpdate.presenterDelete[j].id) {
            updatedOrder.splice(k, 1);
            valuesToUpdate = {...valuesToUpdate, presenterOrder: [...updatedOrder]};
            break;
          }
        }
        
      }
    }

    if (valuesToUpdate.presenterOrder !== undefined) {
      let newPresenters = valuesToUpdate.presentersUpdateAll || [...presenters]
      valuesToUpdate.presenterOrder = validatePresenterOrder(valuesToUpdate.presenterOrder, newPresenters);
      newPresenters = orderPresenters(newPresenters, valuesToUpdate.presenterOrder);
      valuesToUpdate.presentersUpdateAll = newPresenters;
    }

    // TIME
    const adjustResult = adjustPresentersTime(
      (valuesToUpdate.cursor !== undefined) ? valuesToUpdate.cursor : cursor,
      (valuesToUpdate.meetingLength !== undefined) ? valuesToUpdate.meetingLength : meetingLength,
      (valuesToUpdate.presentersUpdateAll !== undefined) ? valuesToUpdate.presentersUpdateAll : presenters
    );
    if (valuesToUpdate.meetingLength !== undefined && !adjustResult.fitsMeetingLength) {
      const { meetingLength: _omitLength, meetingEndTime: _omitEnd, ...rest } = valuesToUpdate;
      return { ...rest, scheduleChangeRejected: true };
    }
    valuesToUpdate = { ...valuesToUpdate, presentersUpdateAll: adjustResult.presenters };

    let newBonusTime = calculateBonusTime(
      (valuesToUpdate.presentersUpdateAll !== undefined) ? valuesToUpdate.presentersUpdateAll : newPresenters,
      (valuesToUpdate.meetingLength !== undefined) ? valuesToUpdate.meetingLength : meetingLength,
      (valuesToUpdate.cursor !== undefined) ? valuesToUpdate.cursor : cursor,
      (valuesToUpdate.bonusTime !== undefined) ? valuesToUpdate.bonusTime : bonusTime
    )
    valuesToUpdate.bonusTime = newBonusTime;
    return valuesToUpdate;
  }

  const applyResolvedLocalState = (valuesToUpdate: StateUpdateObject): StateUpdateObject => {
    if (valuesToUpdate.bonusTime !== undefined) setBonusTime(valuesToUpdate.bonusTime);
    if (valuesToUpdate.cursor !== undefined) updateCursor(valuesToUpdate.cursor);
    if (valuesToUpdate.debugStatus !== undefined) setIsDebugModeEnabled(valuesToUpdate.debugStatus);
    if (valuesToUpdate.displayTimeUnit !== undefined) setIsDisplayTimeUnitSeconds(valuesToUpdate.displayTimeUnit);
    if (valuesToUpdate.meetingEndTime !== undefined) setMeetingEndTime(valuesToUpdate.meetingEndTime);
    if (valuesToUpdate.meetingLength !== undefined) setMeetingLength(valuesToUpdate.meetingLength);
    if (valuesToUpdate.meetingStartTime !== undefined) setMeetingStartTime(valuesToUpdate.meetingStartTime);
    if (valuesToUpdate.presentersUpdateAll !== undefined) setPresenters(valuesToUpdate.presentersUpdateAll);
    if (valuesToUpdate.presenterOrder !== undefined) setPresenterOrder(valuesToUpdate.presenterOrder);

    return valuesToUpdate;
  };

  const updateLocalState = (valuesToUpdate: StateUpdateObject): StateUpdateObject => {
    valuesToUpdate = resolveUpdateImplications(valuesToUpdate);
    if (valuesToUpdate.scheduleChangeRejected) {
      delete valuesToUpdate.scheduleChangeRejected;
      return valuesToUpdate;
    }
    return applyResolvedLocalState(valuesToUpdate);
  }

  const postCommandsWithRetry = async (
    seriesId: string,
    targetSessionId: string,
    commands: Command[],
    editingHistorical: boolean
  ): Promise<SessionState> => {
    try {
      return await postSessionCommands({
        seriesId,
        sessionId: targetSessionId,
        expectedRevision: sessionRevision.current,
        actorId: sessionId.current,
        commands,
      });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        const conflict = err.response.data?.state as SessionState | undefined;
        if (conflict) {
          sessionRevision.current = conflict.revision;
          if (!editingHistorical) {
            setLiveSessionId(conflict.sessionId);
          }
          return postSessionCommands({
            seriesId,
            sessionId: targetSessionId,
            expectedRevision: sessionRevision.current,
            actorId: sessionId.current,
            commands,
          });
        }
      }
      throw err;
    }
  };

  const commandsNeedFullSessionApply = (commands: Command[]): boolean =>
    commands.some((c) => c.type === 'RESET_RUNTIME' || c.type === 'PREPARE_NEXT_SESSION');

  const persistCommands = (commands: Command[], targetSessionId: string, editingHistorical: boolean): Promise<void> => {
    if (meetingID === "" || !targetSessionId) {
      return Promise.resolve();
    }

    const seriesId = meetingID;
    const applyFullSession = commandsNeedFullSessionApply(commands);
    const task = persistChain.current.then(async () => {
      try {
        const state = await postCommandsWithRetry(seriesId, targetSessionId, commands, editingHistorical);
        if (state.revision < sessionRevision.current) {
          return;
        }
        if (applyFullSession) {
          applyServerSession(state, -1);
        } else {
          mergeServerStateAfterLocalWrite(state, editingHistorical);
        }
      } catch (err) {
        console.log("Failed to persist commands", err);
      }
    });

    persistChain.current = task.catch(() => {});
    return task;
  };

  const writeMeetingChange = async (valuesToUpdate: StateUpdateObject): Promise<boolean> => {
    valuesToUpdate = resolveUpdateImplications(valuesToUpdate);
    if (valuesToUpdate.scheduleChangeRejected) {
      return false;
    }
    if (meetingID !== "" && !sessionPersistReady) {
      return false;
    }
    valuesToUpdate = applyResolvedLocalState(valuesToUpdate);
    if (meetingID === "") {
      return true;
    }

    const targetSessionId = viewingSessionId ?? liveSessionId;

    const commands = stateUpdateToCommands(valuesToUpdate, {
      presenters: valuesToUpdate.presentersUpdateAll ?? presenters,
      bonusTime: valuesToUpdate.bonusTime ?? bonusTime,
      cursor: valuesToUpdate.cursor ?? cursor,
      meetingLength: valuesToUpdate.meetingLength ?? meetingLength,
      presenterOrder: valuesToUpdate.presenterOrder ?? presenterOrder,
    });

    if (commands.length === 0) {
      return true;
    }

    await persistCommands(commands, targetSessionId, Boolean(viewingSessionId));
    return true;
  };

  const restartMeeting = async (): Promise<void> => {
    if (!sessionPersistReady || !liveSessionId) {
      return;
    }
    const resetPresenters = presenters.map((p) => ({
      ...p,
      currentDuration: p.initialDuration,
      elapsedTime: 0,
      dbElapsedTime: 0,
      startTime: '',
      endTime: '',
    }));
    const resetBonus = calculateBonusTime(resetPresenters, meetingLength, -1, {
      ...bonusTime,
      currentDuration: meetingLength,
      elapsedTime: 0,
      dbElapsedTime: 0,
      startTime: '',
      endTime: '',
    });
    updateLocalState({
      cursor: -1,
      meetingStartTime: null,
      meetingEndTime: null,
      presentersUpdateAll: resetPresenters,
      bonusTime: resetBonus,
    });
    setElapsedTimeTrigger(!elapsedTimeTrigger);
    await persistCommands([{ type: 'RESET_RUNTIME' }], liveSessionId, false);
  };

  const clearMeetingAgenda = async (): Promise<void> => {
    if (!sessionPersistReady || !liveSessionId) {
      return;
    }
    await persistCommands([
      { type: 'PREPARE_NEXT_SESSION' },
      {
        type: 'SYNC_STATE',
        state: { slots: [], presenterOrder: [], cursor: -1, meetingStartTime: null, meetingEndTime: null },
      },
    ], liveSessionId, false);
  };

  const prepareNextSession = async (): Promise<void> => {
    if (!sessionPersistReady || !liveSessionId) {
      return;
    }
    await persistCommands([{ type: 'PREPARE_NEXT_SESSION' }], liveSessionId, false);
  };

  const validatePresenterOrder = (order:string[], newPresenters:Presenter[]):string[] => {
    let presentersIds = new Set(( newPresenters || [...presenters]).map((presenter)=> presenter.id));
    let validOrder = [];
    for (let i = 0; i < order.length; i++) {
      if (order[i] && presentersIds.has(order[i]) ) {
        validOrder.push(order[i]);
        presentersIds.delete(order[i]);
      }
    }
    if (presentersIds.size > 0) validOrder.concat(Array.from(presentersIds));
    return validOrder;
  }

  /** After our own command persist: sync revision and stolen durations without clobbering live slot timers. */
  const mergeServerStateAfterLocalWrite = (session: SessionState, editingHistorical: boolean): void => {
    if (session.revision < sessionRevision.current) {
      return;
    }
    sessionRevision.current = session.revision;
    if (!editingHistorical) {
      setLiveSessionId(session.sessionId);
    }

    const byId = new Map(session.slots.map((s) => [s.id, s]));
    setPresenters((prev) =>
      prev.map((p) => {
        const s = byId.get(p.id);
        if (!s) {
          return p;
        }
        return {
          ...p,
          currentDuration: s.currentDuration,
          initialDuration: s.initialDuration,
        };
      })
    );
    setBonusTime(slotToPresenter(session.bonus));
  };

  const applyServerSession = (session: SessionState, currentCursor: number): void => {
    sessionRevision.current = session.revision;
    if (session.status === 'live') {
      setLiveSessionId(session.sessionId);
    }

    applySessionToAppState(session, {
      setMeetingName,
      setMeetingLength,
      setPresenters,
      setBonusTime,
      setCursor: (c) => updateCursor(c),
      setPrevCursor,
      setPresenterOrder,
      setIsDebugModeEnabled,
      setIsDisplayTimeUnitSeconds,
      setMeetingStartTime,
      setMeetingEndTime,
    }, currentCursor);

    let newPresenters = orderPresenters(session.slots.map(slotToPresenter), session.presenterOrder);
    const newCursor = session.cursor;
    const timeDenominator = session.debug ? 200 : 1000;

    if (newCursor >= 0 && newCursor < newPresenters.length) {
      const currentTime = new Date().getTime();
      newPresenters[newCursor].startTime = getPresenterStartTime(
        newPresenters[newCursor],
        currentTime,
        newPresenters
      );
      newPresenters[newCursor].elapsedTime = getElapsedTime(
        newPresenters[newCursor],
        timeDenominator,
        currentTime,
        newPresenters
      );
      newPresenters = adjustPresentersTime(newCursor, session.meetingLength, newPresenters).presenters;
    }

    let newBonus = { ...bonusTime, startTime: session.bonus.startTime };
    if (newCursor === newPresenters.length) {
      const currentTime = new Date().getTime();
      const startMs = new Date(newBonus.startTime).getTime();
      const prevElapsedTime = newBonus.dbElapsedTime * timeDenominator;
      newBonus.elapsedTime = Math.floor(((currentTime + timeBuffer.current - startMs) + prevElapsedTime) / timeDenominator);
    }

    const newBonusComputed = calculateBonusTime(newPresenters, session.meetingLength, session.cursor, newBonus);
    setPresenters(newPresenters);
    setBonusTime(newBonusComputed);
    setElapsedTimeTrigger(!elapsedTimeTrigger);
  };

  const syncSessionFromServer = async (
    newMeetingID?: string,
    historicalSessionId?: string | null,
    generation?: number
  ) => {
    const syncGen = generation ?? syncGeneration.current;
    const id = newMeetingID || meetingID;
    if (id === "" || id === "home") {
      if (syncGen === syncGeneration.current) {
        setLoading(false);
      }
      return;
    }
    const histId = historicalSessionId !== undefined ? historicalSessionId : viewingSessionId;

    if (id !== "" && id !== "home") {
      setLoading(true);
    }

    try {
      let session: SessionState;
      if (histId) {
        session = await getSessionStateById(id, histId);
        if (syncGen !== syncGeneration.current) {
          return;
        }
        applyServerSession(session, cursor);
      } else {
        session = await getLiveSessionState(id);
        if (syncGen !== syncGeneration.current) {
          return;
        }
        if (!session.sessionId.startsWith('legacy-')) {
          setLiveSessionId(session.sessionId);
        } else {
          session = await ensureSeriesSession(id, session.name, session.meetingLength);
          if (syncGen !== syncGeneration.current) {
            return;
          }
          setLiveSessionId(session.sessionId);
        }
        applyServerSession(session, cursor);
      }
      if (syncGen === syncGeneration.current) {
        setSessionPersistReady(true);
      }
    } catch (err) {
      console.log(err);
      if (syncGen === syncGeneration.current) {
        setSessionPersistReady(false);
      }
    }
    if (syncGen === syncGeneration.current) {
      setLoading(false);
    }
  };

  const updateStateFromDatabase = async (newMeetingID?: string) => {
    await syncSessionFromServer(newMeetingID, viewingSessionId);
  };

  const loadHistoricalSession = async (sessionId: string) => {
    setViewingSessionId(sessionId);
    setMeetingID(getMeetingIDFromURL() || meetingID);
    await syncSessionFromServer(meetingID, sessionId);
  };

  const loadLiveSession = async () => {
    setViewingSessionId(null);
    await syncSessionFromServer(meetingID, null);
  };

  const getPresenterStartTime = (
    presenter: Presenter,
    currentTime: number,
    roster: Presenter[]
  ): string | number => {
    if (presenter.startTime !== '' && presenter.startTime != null) {
      return presenter.startTime;
    }
    const index = roster.findIndex((e) => e.id === presenter.id);
    if (index >= 0 && roster[index].startTime) {
      return roster[index].startTime;
    }
    return currentTime;
  };

  const getElapsedTime = (
    presenter: Presenter,
    timeDenominator: number,
    currentTime: number,
    roster: Presenter[]
  ): number => {
    const startTimeMs =
      presenter.startTime !== '' ? new Date(presenter.startTime).getTime() : currentTime;
    const prevElapsed = presenter.dbElapsedTime * (isDebugModeEnabled ? 5000 : 1000);
    const elapsedTime = Math.floor(
      ((currentTime + timeBuffer.current - startTimeMs) + prevElapsed) / timeDenominator
    );
    if (Number.isFinite(elapsedTime) && elapsedTime >= 0) {
      return elapsedTime;
    }
    const index = roster.findIndex((e) => e.id === presenter.id);
    if (index >= 0 && index < roster.length) {
      return roster[index].elapsedTime;
    }
    return 0;
  };
  // ==============================================================================================

  //KENDRA WHERE SHOULD THIS GO?

  const endSessionAndSaveHandler = async () => {
    if (!sessionPersistReady || !liveSessionId) return;
    setLoading(true);
    try {
      const fresh = await endSessionAndSave({
        seriesId: meetingID,
        sessionId: liveSessionId,
        actorId: sessionId.current,
      });
      setViewingSessionId(null);
      applyServerSession(fresh, -1);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  };
  //===============================================================================================

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
      <Router>
        <main className="App">
          <Switch>
            <Route exact path={"/"}>
              <Redirect to="/home" />
            </Route>
            <Route exact path={"/home"}>
              <LandingPage
                setHomepageMeetingID={setHomepageMeetingID}
                setMEETING_ID={setMeetingID}
              />
            </Route>
            <Route strict path={"/:id"}>
              <MeetingSMLRer
                meetingName={meetingName}
                meetingLength={meetingLength || 0}
                isDebugModeEnabled={isDebugModeEnabled}
                presenters={presenters || []}
                setPresenters={setPresenters}
                archiveMeeting={endSessionAndSaveHandler}
                prepareNextSession={prepareNextSession}
                viewingSessionId={viewingSessionId}
                loadHistoricalSession={loadHistoricalSession}
                loadLiveSession={loadLiveSession}
                liveSessionId={liveSessionId}
                meetingEditsEnabled={sessionPersistReady}
                bonusTime={bonusTime}
                isAdmin={isAdmin}
                cursor={cursor}
                adjustPresentersTime={adjustPresentersTime}
                restartMeeting={restartMeeting}
                clearMeetingAgenda={clearMeetingAgenda}
                prevCursor={prevCursor}
                cookies={cookies}
                setCookie={setCookie}
                writeMeetingChange={writeMeetingChange}
                updateLocalState={updateLocalState}
                timeBuffer={timeBuffer.current}
                elapsedTimeTrigger={elapsedTimeTrigger}
                meetingEndTime={meetingEndTime}
                meetingStartTime={meetingStartTime}
                calculateBonusTime={calculateBonusTime}
                isDisplayTimeUnitSeconds={isDisplayTimeUnitSeconds}
                meetingID={meetingID}
                setMeetingID={setMeetingID}
                presenterOrder={presenterOrder}
                updateStateFromDatabase={updateStateFromDatabase}
                handleRemoveAll={deleteAllPresenters}
                setLoading={setLoading}
                setIsAdmin={setIsAdmin}
              />
            </Route>
          </Switch>
        </main>
        <Backdrop open={loading} style={{ zIndex: 9999 }}>
          <CircularProgress />
        </Backdrop>
      </Router>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default App;
